/**
 * ============================================================================
 * APP.JS - Main Application Component (FIXED LOGIN FLOW)
 * ============================================================================
 */

import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "./App.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "animate.css";
import LandingPage from "./LandingPage";
import CreateMember from "./pages/CreateMember";
import EditMember from "./pages/EditMember";
import MemberProfile from "./pages/MemberProfile";
import ViewTasks from "./pages/ViewTasks";
import NotFound from "./pages/NotFound";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";
import DashboardLayout from "./components/DashboardLayout";
import LoadingScreen from "./components/LoadingScreen";
import Loader from "./components/Loader";
import Logo from "./components/Logo";
import { CacheProvider } from "./context/CacheContext";

function App() {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');

      const storedUserData = localStorage.getItem('userData');



      if (accessToken && storedUserData) {
        try {
          // First, set user as logged in with cached data
          const cachedUser = JSON.parse(storedUserData);

          setUserData(cachedUser);
          setIsLoggedIn(true);
          setIsLoading(false);

          // Then verify token in background
          const response = await fetch('https://taskscheduler-backend.onrender.com/api/v1/auth/current-user', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
          });
          const result = await response.json();
          if (response.ok) {

            // Update with fresh data from server
            setUserData(result.data.user);
            localStorage.setItem('userData', JSON.stringify(result.data.user));
          } else if (result.statusCode === 401) {
            // Only logout on authentication errors (401/403)

            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('userData');
            setIsLoggedIn(false);
            setUserData(null);
          }
          // For other errors (network, 500, etc.), keep user logged in with cached data
        } catch (error) {


        }
      } else {


        setIsLoggedIn(false);
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleSignIn = async (e) => {
    e.preventDefault();
    const email = e.target.elements?.email?.value ?? e.target.email?.value ?? e.target[0]?.value ?? '';
    const password = e.target.elements?.password?.value ?? e.target.password?.value ?? e.target[1]?.value ?? '';

    // Clear previous errors
    setLoginError('');

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.trim().length === 0) {
      setLoginError('Please enter your email address.');
      return;
    }

    if (!emailRegex.test(email)) {
      setLoginError('Invalid email format. Please enter a valid email address.');
      return;
    }

    // Password validation
    if (!password || password.trim().length === 0) {
      setLoginError('Please enter your password.');
      return;
    }

    if (password.length < 6) {
      setLoginError('Password must be at least 6 characters long.');
      return;
    }

    // Use separate authenticating state to prevent UI flicker
    setIsAuthenticating(true);



    try {
      const response = await fetch('https://taskscheduler-backend.onrender.com/api/v1/auth/login', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email, password }),
      });



      // Check if response is ok (status 200-299)
      if (!response.ok) {
        // Try to parse error response
        try {
          const errorData = await response.json();

          setIsAuthenticating(false);

          // Check if backend provides specific error about email or password
          const errorMessage = errorData.message?.toLowerCase() || '';

          if (errorMessage.includes('email') && errorMessage.includes('not found')) {
            setLoginError('Incorrect email. No account found with this email address.');
          } else if (errorMessage.includes('email')) {
            setLoginError('Incorrect email. Please check your email address.');
          } else if (errorMessage.includes('password')) {
            setLoginError('Incorrect password. Please check your password.');
          } else if (response.status === 401) {
            setLoginError('Invalid credentials. Please check your email and password.');
          } else if (response.status === 400) {
            setLoginError(errorData.message || 'Invalid credentials. Please check your email and password.');
          } else if (response.status === 404) {
            setLoginError('Login service not found. Please contact support.');
          } else if (response.status >= 500) {
            setLoginError('Server error. Please try again later.');
          } else {
            setLoginError(errorData.message || 'Invalid credentials. Please check your email and password.');
          }
        } catch {

          setIsAuthenticating(false);

          // If we can't parse the error, provide user-friendly message based on status
          if (response.status === 401 || response.status === 400) {
            setLoginError('Invalid credentials. Please check your email and password.');
          } else {
            setLoginError(`Server error: ${response.status}. Please try again.`);
          }
        }
        return;
      }

      const data = await response.json();

      if (data.statusCode === 200 && data.data) {
        // Store authentication data
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        localStorage.setItem('userData', JSON.stringify(data.data.user));

        // Reset address bar URL to root so router lands on dashboard/profile instead of 404
        window.history.pushState({}, '', '/');

        // Update state - React will handle the re-render
        setUserData(data.data.user);
        setLoginError('');
        setIsLoggedIn(true);
        setIsAuthenticating(false);


      } else if (data.statusCode === 401 || data.statusCode === 400) {


        setIsAuthenticating(false);

        // Check backend message for specific errors
        const backendMessage = data.message?.toLowerCase() || '';

        if (backendMessage.includes('email') && backendMessage.includes('not found')) {
          setLoginError('Incorrect email. No account found with this email address.');
        } else if (backendMessage.includes('email')) {
          setLoginError('Incorrect email. Please check your email address.');
        } else if (backendMessage.includes('password')) {
          setLoginError('Incorrect password. Please check your password.');
        } else {
          setLoginError('Invalid credentials. Please check your email and password.');
        }
      } else {
        // Other unexpected responses

        setIsAuthenticating(false);
        setLoginError(data.message || 'Invalid credentials. Please check your email and password.');
      }

    } catch (error) {

      setIsAuthenticating(false);

      // Provide more specific error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setLoginError("Cannot connect to server. Please check if the backend is running.");
      } else if (error.name === 'AbortError') {
        setLoginError("Request timeout. Please try again.");
      } else {
        setLoginError(`Connection error: ${error.message}`);
      }
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true); // Show loading screen

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        const res = await fetch('https://taskscheduler-backend.onrender.com/api/v1/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        });

        if (res.ok) {
          // Clear localStorage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');

          // Keep loading screen visible for smooth transition
          await new Promise(resolve => setTimeout(resolve, 500));

          // Update state - React will handle re-render to login page
          setIsLoggedIn(false);
          setUserData(null);
          setIsLoggingOut(false);

          // Navigate to root path
          window.history.pushState({}, '', '/');
        } else {
          setIsLoggingOut(false);
          alert("Logout failed. Please try again.");
        }
      } else {
        // No token, just clear and logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');

        // Keep loading screen visible for smooth transition
        await new Promise(resolve => setTimeout(resolve, 500));

        setIsLoggedIn(false);
        setUserData(null);
        setIsLoggingOut(false);

        // Navigate to root path
        window.history.pushState({}, '', '/');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, clear everything and logout
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');

      // Keep loading screen visible for smooth transition
      await new Promise(resolve => setTimeout(resolve, 500));

      setIsLoggedIn(false);
      setUserData(null);
      setIsLoggingOut(false);

      // Navigate to root path
      window.history.pushState({}, '', '/');
    }
  };




  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Show loading screen during logout
  if (isLoggingOut) {
    return <LoadingScreen isDarkMode={isDarkMode} loadingText="Logging out..." />;
  }

  // Initial loading screen (checking auth on mount)
  if (isLoading) {
    return <LoadingScreen isDarkMode={isDarkMode} loadingText="Loading your workspace..." />;
  }

  // Authenticated user routes
  if (isLoggedIn) {
    const userId = userData?._id || userData?.id;
    const isAdminOrMod = userData?.role === 'admin' || userData?.role === 'moderator';
    const memberTarget = userId ? `/member/${userId}` : '/dashboard';

    return (
      <CacheProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={
              isAdminOrMod
                ? <Navigate to="/dashboard" replace />
                : <Navigate to={memberTarget} replace />
            } />

            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signin" element={<Navigate to="/" replace />} />
            <Route path="/auth" element={<Navigate to="/" replace />} />

            <Route path="/update-password" element={
              <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                <UpdatePassword isDarkMode={isDarkMode} />
              </DashboardLayout>
            } />

            <Route path="/dashboard" element={
              isAdminOrMod ? (
                <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                  <LandingPage handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                </DashboardLayout>
              ) : (
                <Navigate to={memberTarget} replace />
              )
            } />

            <Route path="/create-member" element={
              isAdminOrMod ? (
                <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                  <CreateMember isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                </DashboardLayout>
              ) : (
                <Navigate to={memberTarget} replace />
              )
            } />

            <Route path="/edit-member/:id" element={
              isAdminOrMod ? (
                <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                  <EditMember isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                </DashboardLayout>
              ) : (
                <Navigate to={memberTarget} replace />
              )
            } />

            <Route path="/member/:id" element={
              <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                <MemberProfile isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData} />
              </DashboardLayout>
            } />

            <Route path="/view-tasks" element={
              isAdminOrMod ? (
                <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                  <ViewTasks isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                </DashboardLayout>
              ) : (
                <Navigate to={memberTarget} replace />
              )
            } />

            <Route path="/assign-task" element={
              isAdminOrMod ? (
                <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                  <LandingPage handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                </DashboardLayout>
              ) : (
                <Navigate to={memberTarget} replace />
              )
            } />

            <Route path="*" element={
              <DashboardLayout handleLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} userData={userData}>
                <NotFound isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
              </DashboardLayout>
            } />
          </Routes>
        </BrowserRouter>
      </CacheProvider>
    );
  }

  // Login page and password reset routes (unauthenticated)
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword isDarkMode={isDarkMode} />} />
        <Route path="/reset-password/:token" element={<ResetPassword isDarkMode={isDarkMode} />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    </BrowserRouter>
  );

  function LoginPage() {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);

    return (
      <div className={`login-wrapper ${isDarkMode ? 'dark-mode' : ''} ${isAuthenticating ? 'suppress-animations' : ''}`.trim()}
        style={{ position: 'relative' }}>
        <div className="animated-bg">
          <div className="circle circle-1"></div>
          <div className="circle circle-2"></div>
          <div className="circle circle-3"></div>
        </div>

        <div
          style={{
            position: 'fixed',
            top: window.innerWidth <= 375 ? '0.5rem' : '0.75rem',
            left: window.innerWidth <= 375 ? '0.5rem' : '0.75rem',
            zIndex: 1000,
            transform: window.innerWidth <= 375 ? 'scale(0.5)' : 'scale(0.6)',
            transformOrigin: 'top left'
          }}

        >
          <Logo size="default" isDarkMode={isDarkMode} />
        </div>

        <button
          type="button"
          className="login-theme-toggle"
          onClick={toggleDarkMode}
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
        </button>

        <div className="login-container">
          <div className="branding-side">
            <div className="branding-content">
              <h1 className="brand-title">IEI Students' Chapter CSE Task Manager</h1>
              <p className="brand-subtitle">Organize • Prioritize • Achieve</p>
              <div className="features-list">
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Manage your team effortlessly</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Track tasks in real-time</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Collaborate seamlessly</span>
                </div>
              </div>
            </div>
          </div>

          <div className="form-side">
            <div className="form-content">
              <div className="form-header" style={{ textAlign: 'center' }}>
                <h2>Welcome Back!</h2>
                <p>Please sign in to continue</p>
              </div>

              <form onSubmit={handleSignIn} className="signin-form">
                <div className="input-group">
                  <i className="fas fa-envelope input-icon"></i>
                  <input
                    type="email"
                    name="email"
                    id="signin-email"
                    className="signin-input"
                    placeholder="Email Address"
                    required
                    disabled={isAuthenticating}
                  />
                </div>

                <div className="input-group">
                  <i className="fas fa-lock input-icon"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="signin-password"
                    className="signin-input"
                    placeholder="Password"
                    required
                    disabled={isAuthenticating}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isAuthenticating}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>

                <div className="form-options">
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/forgot-password');
                    }}
                    disabled={isAuthenticating}
                  >
                    Forgot password?
                  </button>
                </div>

                {loginError && (
                  <div className="login-error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{loginError}</span>
                  </div>
                )}

                <button type="submit" className="signin-button" disabled={isAuthenticating}>
                  {isAuthenticating ? (
                    <Loader variant="inline" size="sm" text="Signing in..." isDarkMode={isDarkMode} />
                  ) : (
                    <>
                      Sign In
                      <i className="fas fa-arrow-right"></i>
                    </>
                  )}
                </button>


              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default App;